import type { AxiosInstance } from "axios";
import { ApiError } from "@/core/api/types";
import type { WorkbookExportOptions, WorkbookExportResult } from "./types";

const WORKBOOK_EXPORT_ACCEPTED = 202;
export const WORKBOOK_EXPORT_STATUS = {
  processing: "processing",
  ready: "ready",
} as const;

export const getAiScriptWorkbookExportPath = (
  businessId: string,
  runId: string,
) => `/businesses/${businessId}/ai_script_runs/${runId}/spreadsheet/export`;

/**
 * The 202 body is JSON, but the request asks for a Blob so the 200 can be saved
 * directly. Reading the token back out therefore means parsing the blob, and a
 * body we cannot parse simply means no token — the poll still works, it just
 * re-evaluates the live workbook instead of waiting on its own build.
 */
const readExportToken = async (data: unknown): Promise<string | undefined> => {
  if (!(data instanceof Blob)) return undefined;

  try {
    const parsed: unknown = JSON.parse(await data.text());
    const token = (parsed as { token?: unknown } | null)?.token;
    return typeof token === "string" ? token : undefined;
  } catch {
    return undefined;
  }
};

const decodeWorkbookExportError = async (error: unknown) => {
  if (!(error instanceof ApiError) || !(error.data instanceof Blob))
    return error;

  let body: unknown;
  try {
    body = JSON.parse(await error.data.text());
  } catch {
    return error;
  }
  const message = (body as { error?: unknown } | null)?.error;
  return typeof message === "string"
    ? new ApiError(message, error.status, {
        details: error.details,
        data: body,
      })
    : error;
};

const getWorkbookExportFilename = (
  disposition: unknown,
): string | undefined => {
  if (typeof disposition !== "string") return undefined;
  const encodedFilename = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  let filename = disposition
    .match(/filename="([^"]+)"|filename=([^;]+)/i)
    ?.slice(1)
    .find(Boolean)
    ?.trim();
  if (encodedFilename) {
    try {
      filename = decodeURIComponent(encodedFilename);
    } catch {
      /* Use the plain filename. */
    }
  }
  return filename;
};

export const validateWorkbookExportOptions = ({
  sheetIds,
  exportFormat,
}: WorkbookExportOptions) => {
  if (sheetIds && sheetIds.length === 0)
    throw new Error("Select at least one sheet.");
  if (exportFormat === "csv" && (!sheetIds || new Set(sheetIds).size !== 1)) {
    throw new Error("Select one sheet for CSV.");
  }
};

/** One export request: 202 while the build runs, 200 with the file once cached. */
export const requestAiScriptWorkbookExport = async (
  api: AxiosInstance,
  businessId: string,
  runId: string,
  signal?: AbortSignal,
  token?: string,
  options: WorkbookExportOptions = {},
): Promise<WorkbookExportResult> => {
  validateWorkbookExportOptions(options);
  const params = {
    ...(token ? { token } : {}),
    ...(options.sheetIds ? { "sheet_ids[]": options.sheetIds } : {}),
    ...(options.exportFormat ? { export_format: options.exportFormat } : {}),
  };
  const response = await api
    .get<Blob>(getAiScriptWorkbookExportPath(businessId, runId), {
      responseType: "blob",
      signal,
      params: Object.keys(params).length ? params : undefined,
    })
    .catch(async (error: unknown) => {
      throw await decodeWorkbookExportError(error);
    });

  if (response.status === WORKBOOK_EXPORT_ACCEPTED) {
    return {
      status: WORKBOOK_EXPORT_STATUS.processing,
      token: (await readExportToken(response.data)) ?? token,
    };
  }

  if (response.status < 200 || response.status >= 300) {
    throw new Error("Failed to download payroll result");
  }

  if (response.data instanceof Blob && response.data.size === 0) {
    throw new Error("There are no visible Grid sheets available to download.");
  }

  const filename = getWorkbookExportFilename(
    response.headers?.["content-disposition"],
  );
  return {
    status: WORKBOOK_EXPORT_STATUS.ready,
    file: response.data,
    ...(filename ? { filename } : {}),
  };
};
